import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Alert from '../../../../components/ui/Alert';
import { Skeleton } from '../../../../components/ui/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { deleteTournamentPermanently, getTournamentDeletePreview } from '@/api/TournamentManagement/tournamentApi';
import type { TournamentDeletionPreviewView, TournamentView } from '@/models/tournament';
import { TournamentSeriesChoice } from '@/models/tournament';
import { appDate } from '@/utils/appDateFormat';
import { cn } from '@/utils/cn';
import DeleteTournamentPreview from './DeleteTournamentPreview';

/**
 * CO-922 (owner decisions 2026-10-21): HR deletes a tournament PERMANENTLY, with its matches, assignments,
 * ratings, history, schedule blocks and scheduled shifts. Clock-ins and hourly pay are kept; pay already on
 * a payslip is clawed back on the next payroll.
 *
 * Preview first: nothing can be confirmed until the measured counts are on screen. The confirmation is the
 * tournament NAME (trimmed, case-insensitive, like the server), not "DELETE": recurring occurrences share a
 * name, so the dates sit beside it. A 409 re-renders the dialog with the fresh preview the server returns.
 * Archive (Admin and HR; cancels upcoming matches and unassigns their officials, cannot be undone) lives in
 * ArchiveTournamentModal.
 */

const ERR_NAME = 'TOURNAMENT_DELETE_NAME_MISMATCH';
const ERR_SERIES = 'TOURNAMENT_DELETE_SERIES_CHOICE_REQUIRED';
const ERR_STALE = 'TOURNAMENT_DELETE_STALE_PREVIEW';
const ERR_BLOCKED = 'TOURNAMENT_DELETE_BLOCKED';

const norm = (s: string) => s.trim().toLocaleLowerCase();

interface DeleteTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: TournamentView | null;
  onDeleted?: () => void;
}

const DeleteTournamentModal: React.FC<DeleteTournamentModalProps> = ({ isOpen, onClose, tournament, onDeleted }) => {
  const { t } = useTranslation('tournament');
  const [preview, setPreview] = useState<TournamentDeletionPreviewView | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [seriesChoice, setSeriesChoice] = useState<TournamentSeriesChoice | null>(null);
  const [seriesError, setSeriesError] = useState(false);
  const [stale, setStale] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadPreview = useCallback(async (id: number) => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const result = await getTournamentDeletePreview(id);
      if (result.errors.length > 0 || !result.payload) {
        setPreviewError(result.errors[0]?.description || t('cascadeDelete.previewFailed'));
        return;
      }
      setPreview(result.payload);
    } catch {
      setPreviewError(t('cascadeDelete.previewFailed'));
    } finally {
      setLoadingPreview(false);
    }
  }, [t]);

  // Fresh state and a fresh preview on every open: the counts must describe the data as it is now.
  useEffect(() => {
    if (!isOpen || !tournament) return;
    setPreview(null);
    setConfirmText('');
    setConfirmTouched(false);
    setSeriesChoice(null);
    setSeriesError(false);
    setStale(false);
    setApiError(null);
    void loadPreview(tournament.id);
  }, [isOpen, tournament, loadPreview]);

  const name = preview?.name ?? tournament?.name ?? '';
  const nameMatches = !!preview && norm(confirmText) === norm(preview.name);
  const isRecurring = !!preview?.recurring?.isRecurring;
  const blocked = (preview?.blockers.length ?? 0) > 0;
  const canSubmit = !!preview && !blocked && nameMatches && (!isRecurring || seriesChoice !== null) && !isDeleting;
  const showNameError = confirmTouched && confirmText.trim() !== '' && !nameMatches;

  const handleDelete = async () => {
    if (!tournament || !preview || !canSubmit) return;
    setIsDeleting(true);
    setApiError(null);
    setStale(false);
    try {
      const result = await deleteTournamentPermanently(tournament.id, {
        confirmName: confirmText,
        fingerprint: preview.fingerprint,
        ...(isRecurring && seriesChoice !== null ? { seriesChoice } : {}),
      });
      const err = result.errors[0];
      if (!err && result.payload?.deleted) {
        onDeleted?.();
        onClose();
        return;
      }
      if ((err?.code === ERR_STALE || err?.code === ERR_BLOCKED) && result.payload?.preview) {
        setPreview(result.payload.preview);
        setStale(err.code === ERR_STALE);
        return;
      }
      if (err?.code === ERR_NAME) { setConfirmTouched(true); return; }
      if (err?.code === ERR_SERIES) { setSeriesError(true); return; }
      setApiError(err?.description || t('Failed to delete tournament'));
    } catch {
      setApiError(t('cascadeDelete.genericError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const dates = preview ?? tournament;
  const dateRange = dates ? `${appDate(dates.startDate)} - ${appDate(dates.endDate)}` : '';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isDeleting) onClose(); }}>
      <DialogContent
        size="default"
        // All dismissal paths lock together while the delete is in flight (ui-standards.md).
        onEscapeKeyDown={(e) => { if (isDeleting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isDeleting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isDeleting) e.preventDefault(); }}
        hideCloseButton={isDeleting}
        data-testid="delete-tournament-modal"
      >
        <DialogHeader className="border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <Icon name="Trash2" size={20} className="shrink-0" />
            {t('cascadeDelete.title')}
          </DialogTitle>
          <DialogDescription className="mt-1">
            <span className="font-medium text-foreground break-words" data-testid="delete-tournament-name">{name}</span>
            {dateRange && <span className="ms-2 whitespace-nowrap" dir="ltr">{dateRange}</span>}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('cascadeDelete.intro')}</p>

          {stale && (
            <Alert variant="warning" data-testid="delete-tournament-stale">
              <p className="text-sm">{t('cascadeDelete.stale')}</p>
            </Alert>
          )}

          {apiError && (
            <Alert variant="error" role="alert" data-testid="delete-tournament-error">
              <p className="text-sm">{apiError}</p>
            </Alert>
          )}

          {loadingPreview && !preview && (
            <div className="space-y-3" aria-busy="true" aria-label={t('cascadeDelete.loadingPreview')} data-testid="delete-tournament-preview-loading">
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
            </div>
          )}

          {previewError && !preview && (
            <Alert variant="error" role="alert" data-testid="delete-tournament-preview-error">
              <div className="space-y-2">
                <p className="text-sm">{previewError}</p>
                {tournament && (
                  <Button type="button" variant="outline" size="sm" onClick={() => loadPreview(tournament.id)} data-testid="delete-tournament-preview-retry">
                    {t('Try Again')}
                  </Button>
                )}
              </div>
            </Alert>
          )}

          {preview && blocked && (
            <Alert variant="error" role="alert" data-testid="delete-tournament-blockers">
              <div className="space-y-1">
                <p className="text-sm font-semibold">{t('cascadeDelete.blockedTitle')}</p>
                {preview.blockers.includes('OPEN_SESSION') && (
                  <p className="text-sm" data-testid="delete-tournament-blocker-open-session">{t('cascadeDelete.blocker.openSession')}</p>
                )}
                {preview.blockers.includes('UNQUANTIFIABLE_PAY_LINK') && (
                  <p className="text-sm" data-testid="delete-tournament-blocker-pay-link">{t('cascadeDelete.blocker.unquantifiablePay')}</p>
                )}
              </div>
            </Alert>
          )}

          {preview && <DeleteTournamentPreview preview={preview} />}

          {preview && isRecurring && (
            <fieldset className="space-y-2" data-testid="delete-tournament-series">
              <legend className="text-sm font-medium text-foreground mb-2">{t('cascadeDelete.series.legend')}</legend>
              {[
                { value: TournamentSeriesChoice.Continue, label: t('cascadeDelete.series.continue'), hint: t('cascadeDelete.series.continueHint'), id: 'continue' },
                { value: TournamentSeriesChoice.EndSeries, label: t('cascadeDelete.series.end'), hint: t('cascadeDelete.series.endHint'), id: 'end' },
              ].map((opt) => {
                const selected = seriesChoice === opt.value;
                return (
                  <label
                    key={opt.id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors duration-150',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                      isDeleting && 'cursor-not-allowed opacity-60',
                    )}
                    data-testid={`delete-tournament-series-${opt.id}`}
                  >
                    <input
                      type="radio"
                      name="cascade-delete-series"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      checked={selected}
                      disabled={isDeleting}
                      onChange={() => { setSeriesChoice(opt.value); setSeriesError(false); }}
                    />
                    <span className="min-w-0">
                      <span className={cn('block text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                    </span>
                  </label>
                );
              })}
              {seriesError && (
                <p role="alert" className="text-sm text-destructive" data-testid="delete-tournament-series-error">{t('cascadeDelete.series.required')}</p>
              )}
            </fieldset>
          )}

          {preview && !blocked && (
            <Input
              type="text"
              label={t('cascadeDelete.confirmLabel')}
              description={t('cascadeDelete.confirmHint')}
              error={showNameError ? t('cascadeDelete.nameMismatch') : undefined}
              value={confirmText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
              onBlur={() => setConfirmTouched(true)}
              placeholder={preview.name}
              disabled={isDeleting}
              autoComplete="off"
              spellCheck={false}
              data-testid="delete-tournament-confirm-input"
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isDeleting} data-testid="delete-tournament-cancel-btn">
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!canSubmit}
            loading={isDeleting}
            data-testid="tournament-delete-btn"
          >
            {t('cascadeDelete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTournamentModal;
